import React, { useCallback, useState } from 'react';
import { Price } from 'ui/Price';
import { ProductLineWithPrice } from './ProductLineWithPrice';
import { BookingBuilder, EUploadTag, ICompany, ProductSetAccommodation } from 'services/BackendApi';
import { ERateType, RateTag } from 'ui/RateTag/RateTag';
import { isNil, uniq } from 'lodash-es';
import { LodgingSummary } from 'interfaces';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { useSelector } from 'react-redux';
import { companyDataSelector } from 'store/modules/companyInfo';
import { isRefundable as isRefundableFn } from 'common-lib/cancellation-policy-composer';
import { isAdult } from 'utils';
import { ERefundabilityTagStatus, RefundabilityTag } from 'ui/RefundabilityTag/RefundabilityTag';
import { IBasketBuildL4 } from 'services/BackendApi/types/Basket';
import { SvgIcon } from 'ui/SvgIcon';
import MapIcon from 'ui/Icons/components/Map.component';
import { theme } from '../../../tailwind.config';
import ArticleIcon from 'ui/Icons/components/Article.component';
import PictureIcon from 'ui/Icons/components/Picture.component';
import VideoIcon from 'ui/Icons/components/Video.component';
import { openLink } from './helpers';
import { EVideoPlatform, VideoModal } from 'components/AccommodationCard/VideoModal';
import { BasketHotelPhotoGalleryModal } from './BasketHotelPhotoGalleryModal';
import { pluralize } from 'utils/string';
import { AdultComponent as AdultIcon, RoomSizeComponent as RoomSizeIcon } from 'ui/Icons';
import { VerticalLine } from 'ui/VerticalLine';
import { HidingTooltip } from 'ui/Tooltip';
import { useTranslation } from 'react-i18next';
import { OtherOptions } from './OtherOptions';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';
import { IOnHoldPerRoomInfo } from './BasketItem';
import { OnHoldInfoSecondary } from './OnHoldInfoSecondary';
import { RoomBenefits } from 'components/AccommodationCard/RoomBenefits';
import { AccommodationDatesGuests } from './BookingAccommodation/AccommodationDatesGuests';
import { ExpandableArea } from 'ui/ExpandableArea/ExpandableArea';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import { AccommodationMealPlan } from './BookingAccommodation/AccommodationMealPlan';
import { AccommodationExtraPersonSupplements } from './BookingAccommodation/AccommodationExtraPersonSupplements';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { MobileBottomDrawer } from 'ui/MobileBottomDrawer/MobileBottomDrawer';
import { useCurrencyConversion } from 'context/CurrencyConversion/CurrencyConversionContext';

interface IBookingAccommodationProps {
  booking: BookingBuilder;
  accommodation: ProductSetAccommodation;
  lodgingSummaries: LodgingSummary[];
  startDate: string;
  endDate: string;
  accommodationIndex: number;
  build: IBasketBuildL4;
  onHoldInfo: IOnHoldPerRoomInfo | undefined;
  shouldHidePricing?: boolean;
}

export const BookingAccommodation: React.FC<IBookingAccommodationProps> = React.memo(
  ({
    booking,
    startDate,
    endDate,
    lodgingSummaries,
    accommodation,
    accommodationIndex,
    build,
    onHoldInfo,
    shouldHidePricing = false,
  }) => {
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const { convert, displayCurrencyCode } = useCurrencyConversion();
    const effectiveCurrencyCode = displayCurrencyCode || booking.response.currency;
    const company = useSelector(companyDataSelector) as ICompany;
    const isTa: boolean = useSelector(AuthSelectors.isTA);
    const selectedMealPlan = accommodation.availableSubProductSets['Meal Plan'].find(item => item.selected);
    const mealPlanOtherOptions = accommodation.availableSubProductSets['Meal Plan']
      .filter(item => !item.selected)!
      .map(mp => ({
        title: mp.products[0].name,
        price: mp.total != null ? String(convert(mp.total)) : null,
        currencyCode: effectiveCurrencyCode,
        isOnRequest: mp.isOnRequestOrPartiallyOnRequest,
      }));

    const extraPersonSupplements = accommodation.availableSubProductSets['Supplement'].filter(item => item.selected);

    const priceClassName = 'font-hurmegeometric-sans text-15px leading-18px text-right mt-5px m-0';
    const isInstantBook =
      lodgingSummaries[accommodationIndex].availableToInstantBook && (!isTa || (isTa && company?.enableInstantBooking));
    const offers = booking.response.appliedOfferNames;

    const expenseInfo = booking.response.expenseInfosGroupedByRoom?.[accommodationIndex];
    const isRefundable = isNil(expenseInfo) ? false : isRefundableFn(expenseInfo);

    const [isPhotoModalOpen, setPhotoModalOpen] = useState(false);
    const roomPhotos = build.uploads?.filter(
      item =>
        [EUploadTag.PHOTO, EUploadTag.FEATURED_PHOTO].includes(item?.tag as EUploadTag) &&
        item?.ownerUuid === accommodation.products[0].uuid
    );
    const [isVideoModalOpen, setVideoModalOpen] = useState(false);

    const floorPlan = build.uploads?.find(
      item => item?.tag === EUploadTag.FLOOR_PLAN && item?.ownerUuid === accommodation.products[0].uuid
    );
    const handleFloorPlanClick = useCallback(() => {
      floorPlan?.url && openLink(floorPlan.url);
    }, [floorPlan?.url, floorPlan]);
    const roomInfoPdf = build.uploads?.find(
      item => item?.tag === EUploadTag.ACCOMMODATION_INFORMATION && item?.ownerUuid === accommodation.products[0].uuid
    );

    const occupancyResult = accommodation.occupancyResult;

    const renderOccupancyTooltip = useCallback(() => {
      return (
        <div
          className="occupancy-tooltip text-13px leading-16px text-black p-10px shadow-pe6"
          style={{ maxWidth: '1600px' }}
        >
          {occupancyResult?.occupancy?.limits.map(limit => {
            const { name, minimum, maximum } = limit;
            const groupName = isAdult(name) ? t('adult_plural') : t(`${name}_plural`) || name;
            const limitText = `${groupName} - ${t('labels.max')} ${maximum} ${t('labels.min')} ${minimum}`;
            return (
              <span className="whitespace-nowrap" key={limit.name}>
                {limitText} <br />
              </span>
            );
          })}
        </div>
      );
    }, [occupancyResult, t]);

    const handleRoomInfoPdfClick = useCallback(() => {
      roomInfoPdf?.url && openLink(roomInfoPdf?.url);
    }, [roomInfoPdf]);
    const openPhotoModal = useCallback(() => {
      setPhotoModalOpen(true);
    }, []);
    const closePhotoModal = useCallback(() => {
      setPhotoModalOpen(false);
    }, []);
    const openVideoModal = useCallback(() => {
      setVideoModalOpen(true);
    }, []);
    const closeVideoModal = useCallback(() => {
      setVideoModalOpen(false);
    }, []);

    const selectedMealPlanHasDescription = (selectedMealPlan?.products || []).some(item => item.meta.description);

    const rateInclusions = build.initialBuildResponse.potentialBooking?.Accommodation?.[accommodationIndex]?.features;
    const hasRateInclusions = rateInclusions && rateInclusions.length > 0;

    return (
      <div className="confirmation-accommodation px-5 py-15px border-solid border-b border-gray-20">
        <div className="flex justify-between items-center">
          <p className="accommodation-title font-hurmegeometric-sans text-flint text-13px leading-16px uppercase font-bold m-0">
            {accommodation.products[0].name}
          </p>
          {onHoldInfo && (
            <OnHoldInfoSecondary isValid={onHoldInfo.isValid} systemBookingId={onHoldInfo.systemBookingId} />
          )}
        </div>
        {(accommodation.products[0].meta.size || occupancyResult?.occupancy) && (
          <div className="occupancy-and-size flex items-center gap-[10px] h-[24px]">
            {accommodation.products[0].meta.size && (
              <>
                <RoomSizeIcon icon={theme.colors['brown-100']} />
                <span className="text-sm leading-17px text-gold-light">
                  {accommodation.products[0].meta.size} sq. m.
                </span>
              </>
            )}
            {accommodation.products[0].meta.size && occupancyResult?.occupancy && (
              <VerticalLine height="15px" color={theme.colors['gray-18']} />
            )}
            {occupancyResult && occupancyResult?.occupancy && (
              <>
                <HidingTooltip renderTooltipContent={renderOccupancyTooltip} position="bottom-right">
                  <span className="flex">
                    <AdultIcon icon={theme.colors['brown-100']} />
                  </span>
                </HidingTooltip>
                <span className="text-sm leading-17px text-gold-light">
                  x{occupancyResult.occupancy.standardOccupancy} (max. {occupancyResult.occupancy.maximumPeople})
                </span>
              </>
            )}
          </div>
        )}
        {/* room floor plan, video and amenities icons */}
        {(floorPlan?.url || roomInfoPdf?.url || roomPhotos.length > 0 || accommodation.products[0].vimeoVideoId) && (
          <div className="extra-details-icons flex justify-start items-center mt-[7px] mb-[12px] gap-[15px] ml-[-5px]">
            {floorPlan?.url ? (
              <div
                className="extra-detail-item flex items-center gap-[3px] cursor-pointer"
                onClick={handleFloorPlanClick}
              >
                <span className="flex justify-center items-center cursor-pointer w-6 h-6 hover:bg-gray-20">
                  <SvgIcon
                    IconComponent={MapIcon}
                    defaultFill={theme.colors['brown-100']}
                    hoverFill={theme.colors['brown-hard']}
                    defaultBackground={theme.colors['transparent']}
                    hoverBackground={theme.colors['gray-20']}
                    width="16px"
                    height="16px"
                  />
                </span>
                <span className="font-hurmegeometric-sans text-[13px] leading-[20px] text-brown-100 underline select-none">
                  Floor Plan
                </span>
              </div>
            ) : null}
            {roomInfoPdf?.url ? (
              <div
                className="extra-detail-item flex items-center gap-[3px] cursor-pointer"
                onClick={handleRoomInfoPdfClick}
              >
                <HidingTextTooltip
                  tooltipContent="Room Info / Bedding"
                  position="bottom-right"
                  tooltipContentClassname="pdf-tooltip"
                >
                  <span className="flex justify-center items-center cursor-pointer w-6 h-6 hover:bg-gray-20">
                    <SvgIcon
                      IconComponent={ArticleIcon}
                      defaultFill={theme.colors['brown-100']}
                      hoverFill={theme.colors['brown-hard']}
                      defaultBackground={theme.colors['transparent']}
                      hoverBackground={theme.colors['gray-20']}
                      width="16px"
                      height="16px"
                    />
                  </span>
                </HidingTextTooltip>
                <span className="font-hurmegeometric-sans text-[13px] leading-[20px] text-brown-100 underline select-none">
                  Amenities
                </span>
              </div>
            ) : null}
            {roomPhotos.length > 0 && (
              <div className="extra-detail-item flex items-center gap-[3px] cursor-pointer" onClick={openPhotoModal}>
                <span className="flex justify-center items-center cursor-pointer w-6 h-6 hover:bg-gray-20">
                  <SvgIcon
                    IconComponent={PictureIcon}
                    defaultFill={theme.colors['brown-100']}
                    hoverFill={theme.colors['brown-hard']}
                    defaultBackground={theme.colors['transparent']}
                    hoverBackground={theme.colors['gray-20']}
                    width="16px"
                    height="16px"
                  />
                </span>
                <span className="font-hurmegeometric-sans text-[13px] leading-[20px] text-brown-100 underline select-none">
                  Pictures
                </span>
              </div>
            )}
            {accommodation.products[0].vimeoVideoId && (
              <div className="extra-detail-item flex items-center gap-[3px] cursor-pointer" onClick={openVideoModal}>
                <span className="flex justify-center items-center cursor-pointer w-6 h-6 hover:bg-gray-20">
                  <SvgIcon
                    IconComponent={VideoIcon}
                    defaultFill={theme.colors['brown-100']}
                    hoverFill={theme.colors['brown-hard']}
                    defaultBackground={theme.colors['transparent']}
                    hoverBackground={theme.colors['gray-20']}
                    width="16px"
                    height="16px"
                  />
                </span>
                <span className="font-hurmegeometric-sans text-[13px] leading-[20px] text-brown-100 underline select-none">
                  Video
                </span>
              </div>
            )}
          </div>
        )}
        {/* Room Benefits */}
        {accommodation.products[0].meta.moreInformation ? (
          <ExpandableArea
            title="Room Benefits"
            containerClassName="o:p-[10px] bg-ivory rounded-[3px] border border-solid border-gray-20 o:mb-[10px]"
            titleRowClassName="o:justify-between"
            titleClassName="o:no-underline"
          >
            <HtmlStringRenderer content={accommodation.products[0].meta.moreInformation} fallbackComponent="span" />
          </ExpandableArea>
        ) : null}
        {/* refundability tag and instant book tag */}
        <div className="flex justify-between items-center">
          <div className="refundability-tag">
            {isRefundable !== null && (
              <RefundabilityTag
                refundabilityStatus={
                  isRefundable ? ERefundabilityTagStatus.REFUNDABLE : ERefundabilityTagStatus.NON_REFUNDABLE
                }
                className="acc-free-sale-rates-tag flex items-center m-0"
              />
            )}
          </div>
          {isInstantBook ? <RateTag rateType={ERateType.INSTANT_BOOK} /> : null}
        </div>
        <div className="accommodation-data flex items-stretch justify-between">
          <div className="accommodation-data-left">
            <AccommodationDatesGuests
              requestedAccommodation={booking.request.Accommodation[accommodationIndex]}
              offers={offers}
              accommodation={accommodation}
            />
          </div>
          {!shouldHidePricing && (
            <div className="accommodation-data-right">
              <Price
                total={accommodation.total != null ? String(convert(accommodation.total)) : null}
                totalBeforeDiscount={accommodation.totalBeforeDiscount != null ? String(convert(accommodation.totalBeforeDiscount)) : null}
                currencyCode={effectiveCurrencyCode}
                isOnRequest={accommodation.isOnRequestOrPartiallyOnRequest}
                totalClassName={`${priceClassName} text-flint`}
                totalBeforeDiscountClassName={`${priceClassName} text-gold-light line-through`}
                totalAfterDiscountClassName={`${priceClassName} text-red-15`}
                isOnRequestClassName={`${priceClassName} text-flint`}
                offersAppliedClassName="h-full justify-between"
              />
            </div>
          )}
        </div>

        {/* Rate Inclusions - only show if features array exists and has items */}
        {hasRateInclusions && (
          <div className="rate-inclusions mt-[10px]">
            <ProductLineWithPrice
              currencyCode={effectiveCurrencyCode}
              className="rate-inclusions-line"
              details={{
                uploads: [],
                title: 'Rate Inclusions',
                description: rateInclusions.join('\n'),
              }}
              showDescription={true}
              shouldHidePricing={true}
            />
          </div>
        )}

        {selectedMealPlan && (
          <AccommodationMealPlan
            mealPlan={selectedMealPlan}
            currencyCode={effectiveCurrencyCode}
            uploads={build.uploads}
            shouldHidePricing={shouldHidePricing}
          />
        )}
        {mealPlanOtherOptions.length > 0 && (
          <OtherOptions options={mealPlanOtherOptions} shouldHidePricing={shouldHidePricing} />
        )}
        {extraPersonSupplements.length > 0 ? (
          <AccommodationExtraPersonSupplements
            extraPersonSupplements={extraPersonSupplements}
            currencyCode={effectiveCurrencyCode}
            shouldHidePricing={shouldHidePricing}
          />
        ) : null}
        {isPhotoModalOpen && roomPhotos?.length > 0 && (
          isMobile ? (
            <MobileBottomDrawer
              isOpen={isPhotoModalOpen}
              onClose={closePhotoModal}
              title={accommodation.products[0].name}
            >
              <BasketHotelPhotoGalleryModal
                title={accommodation.products[0].name}
                photos={roomPhotos}
                onClose={closePhotoModal}
                renderAsContent
              />
            </MobileBottomDrawer>
          ) : (
            <BasketHotelPhotoGalleryModal
              title={accommodation.products[0].name}
              photos={roomPhotos}
              onClose={closePhotoModal}
            />
          )
        )}

        {isVideoModalOpen && accommodation.products[0].vimeoVideoId && (
          isMobile ? (
            <MobileBottomDrawer
              isOpen={isVideoModalOpen}
              onClose={closeVideoModal}
              title={accommodation.products[0].name}
            >
              <VideoModal
                videoId={accommodation.products[0].vimeoVideoId}
                title={accommodation.products[0].name}
                type={EVideoPlatform.VIMEO}
                onClose={closeVideoModal}
                renderAsContent
              />
            </MobileBottomDrawer>
          ) : (
            <VideoModal
              videoId={accommodation.products[0].vimeoVideoId}
              title={accommodation.products[0].name}
              type={EVideoPlatform.VIMEO}
              onClose={closeVideoModal}
            />
          )
        )}
      </div>
    );
  }
);
